/**
 * Agent Registry Redux Slice - APB-1964
 * Manages state for agent registry, approvals, and governance
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  AgentRegistryEntry,
  AgentRegistryState,
  AgentRegistryCreateRequest,
  AgentRegistryUpdateRequest,
  AgentRegistryFilters,
} from '../types/index.js';
import {
  listAgents,
  getAgent,
  createAgent,
  updateAgent,
  deleteAgent,
  approveAgent,
} from '../services/agentRegistryApi.js';

const initialState: AgentRegistryState = {
  agents: [],
  selectedAgent: null,
  total: 0,
  loading: false,
  error: null,
  filters: {
    limit: 50,
    offset: 0,
  },
};

/**
 * Async thunk to fetch agents list with filters
 */
export const fetchAgents = createAsyncThunk(
  'agentRegistry/fetchAgents',
  async (filters: AgentRegistryFilters = {}, { rejectWithValue }) => {
    try {
      return await listAgents(filters);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch agents'
      );
    }
  }
);

/**
 * Async thunk to fetch a single agent by ID
 */
export const fetchAgent = createAsyncThunk(
  'agentRegistry/fetchAgent',
  async (agentId: string, { rejectWithValue }) => {
    try {
      return await getAgent(agentId);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch agent'
      );
    }
  }
);

/**
 * Async thunk to create a new agent
 */
export const createAgentEntry = createAsyncThunk(
  'agentRegistry/createAgent',
  async (request: AgentRegistryCreateRequest, { rejectWithValue }) => {
    try {
      return await createAgent(request);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to create agent'
      );
    }
  }
);

/**
 * Async thunk to update an agent
 */
export const updateAgentEntry = createAsyncThunk(
  'agentRegistry/updateAgent',
  async (
    {
      agentId,
      request,
    }: { agentId: string; request: AgentRegistryUpdateRequest },
    { rejectWithValue }
  ) => {
    try {
      return await updateAgent(agentId, request);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to update agent'
      );
    }
  }
);

/**
 * Async thunk to delete an agent
 */
export const deleteAgentEntry = createAsyncThunk(
  'agentRegistry/deleteAgent',
  async (agentId: string, { rejectWithValue }) => {
    try {
      await deleteAgent(agentId);
      return agentId;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to delete agent'
      );
    }
  }
);

/**
 * Async thunk to approve an agent
 */
export const approveAgentEntry = createAsyncThunk(
  'agentRegistry/approveAgent',
  async (agentId: string, { rejectWithValue }) => {
    try {
      return await approveAgent(agentId);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to approve agent'
      );
    }
  }
);

const agentRegistrySlice = createSlice({
  name: 'agentRegistry',
  initialState,
  reducers: {
    // Set filter criteria
    setFilters: (state, action: PayloadAction<AgentRegistryFilters>) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    // Clear selected agent
    clearSelectedAgent: (state) => {
      state.selectedAgent = null;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Agents
    builder
      .addCase(fetchAgents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgents.fulfilled, (state, action) => {
        state.loading = false;
        // Defensive: Ensure agents is always an array
        state.agents = Array.isArray(action.payload.agents) ? action.payload.agents : [];
        state.total = action.payload.total ?? 0;
      })
      .addCase(fetchAgents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Reset to safe defaults on error
        state.agents = [];
        state.total = 0;
      });

    // Fetch Single Agent
    builder
      .addCase(fetchAgent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgent.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedAgent = action.payload;
      })
      .addCase(fetchAgent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create Agent
    builder
      .addCase(createAgentEntry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAgentEntry.fulfilled, (state, action) => {
        state.loading = false;
        state.agents.push(action.payload);
        state.total += 1;
      })
      .addCase(createAgentEntry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update Agent
    builder
      .addCase(updateAgentEntry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAgentEntry.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.agents.findIndex((a) => a.id === action.payload.id);
        if (index >= 0) {
          state.agents[index] = action.payload;
        }
        if (state.selectedAgent?.id === action.payload.id) {
          state.selectedAgent = action.payload;
        }
      })
      .addCase(updateAgentEntry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete Agent
    builder
      .addCase(deleteAgentEntry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAgentEntry.fulfilled, (state, action) => {
        state.loading = false;
        state.agents = state.agents.filter((a) => a.id !== action.payload);
        state.total -= 1;
        if (state.selectedAgent?.id === action.payload) {
          state.selectedAgent = null;
        }
      })
      .addCase(deleteAgentEntry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Approve Agent
    builder
      .addCase(approveAgentEntry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveAgentEntry.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.agents.findIndex((a) => a.id === action.payload.id);
        if (index >= 0) {
          state.agents[index] = action.payload;
        }
        if (state.selectedAgent?.id === action.payload.id) {
          state.selectedAgent = action.payload;
        }
      })
      .addCase(approveAgentEntry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setFilters, clearSelectedAgent, clearError } =
  agentRegistrySlice.actions;

export default agentRegistrySlice.reducer;
